import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../utils/api';

/*
 * A 1:1 port of the Scale portal's Account -> Reminders tab
 * (scale-portal/src/components/template/Account/Tabs/ReminderSettingsTab.jsx).
 *
 * Everything a mediator sees on their own Reminders tab, a case manager sees
 * here: same three cards, same copy, same limits, same add/remove gating, same
 * confirm dialog, and the same save behaviour — each field persists on blur,
 * with no Save button.
 *
 * Two deliberate differences, both invisible while things work:
 *   1. A failed save shows an error. Scale catches the error and logs it to the
 *      console, so the field keeps the typed value and the user is told nothing.
 *   2. The whole schedule object is sent and the backend merges it over what is
 *      stored. Scale rebuilds the object from the three known categories and
 *      column-replaces, which deletes `reminder_schedules.mediation` — a live
 *      key that gates an email template.
 */

const CATEGORIES = { ONBOARDING: 'onboarding', BRIEF: 'brief', PAYMENT: 'payment' };
const MAX_REMINDERS = 4;
const MAX_REMINDER_DAYS = 45;

const ONBOARDING_HELPER =
  'Start with an initial reminder, then add follow-ups as needed. Each additional reminder is sent a set number of days after the previous one. You can remove follow-ups at any time.';
const BRIEF_HELPER =
  'Start with an initial reminder, then add follow-ups as needed. All brief reminders are sent a set number of days before the mediation date. Each follow-up must be scheduled closer to the mediation than the previous one. You can remove follow-ups at any time.';
const PAYMENT_HELPER =
  'Start with an invoice due date reminder, then add follow-ups as needed. The first reminder is sent a set number of days after the invoice is created. Subsequent reminders are sent after the invoice due date, with each follow-up scheduled a set number of days after the previous one. You can remove follow-ups at any time.';

const getOrdinalLabel = (n) => {
  const suffix = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (suffix[(v - 20) % 10] || suffix[v] || suffix[0]);
};

const getPaymentReminderLabel = (index, labelOrder) =>
  (index === 0 ? 'Invoice Due Date Reminder' : `${labelOrder[index - 1]} payment reminder`);

const keyFor = (index) => `reminder_${index + 1}_days`;

// Highest populated index + 1, minimum 1 — so sparse data renders as gaps
// rather than being silently compacted, matching Scale.
const initialActiveCount = (category) => {
  let highest = -1;
  Object.keys(category || {}).forEach((k) => {
    const m = k.match(/^reminder_(\d+)_days$/);
    if (!m) return;
    const i = parseInt(m[1], 10) - 1;
    const val = category[k];
    if (val !== null && val !== undefined && val !== '' && i > highest) highest = i;
  });
  return highest >= 0 ? highest + 1 : 1;
};

function SectionHeading({ title, subtitle }) {
  return (
    <div className="flex flex-col gap-y-1 py-2">
      <h3 className="text-xl font-semibold text-[#0e1e20]" style={{ fontFamily: "'Inter Tight', sans-serif" }}>{title}</h3>
      {subtitle && (
        <p className="text-sm font-normal text-[#5b5a64]" style={{ fontFamily: 'Inter, sans-serif' }}>{subtitle}</p>
      )}
    </div>
  );
}

function ConfirmDialog({ open, onCancel, onConfirm }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(15,23,42,0.45)' }}
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white rounded-md shadow-lg p-6 mx-4" style={{ maxWidth: 460, width: '100%' }}>
        <h4 className="text-base font-semibold text-[#0e1e20]" style={{ fontFamily: "'Inter Tight', sans-serif" }}>
          Changes to reminder schedules apply only to new and upcoming cases.
        </h4>
        <div className="flex items-center justify-end gap-2 mt-6">
          <button
            type="button"
            onClick={onCancel}
            className="button button-default button-sm"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="button button-solid button-sm"
            style={{ fontFamily: 'Inter, sans-serif', background: '#115e59' }}
          >
            Proceed
          </button>
        </div>
      </div>
    </div>
  );
}

function ReminderRow({
  label, timingLabel, value, error, onChange, onBlurUpdate,
  canRemove, onRemove, isDisabled, disabledReason,
}) {
  return (
    <div className="flex flex-col gap-y-2 border border-gray-100 rounded-md p-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <span className="text-sm font-semibold text-[#0e1e20]" style={{ fontFamily: 'Inter, sans-serif' }}>{label}</span>
          <span className="text-xs text-gray-500 block" style={{ fontFamily: 'Inter, sans-serif' }}>{timingLabel}</span>
        </div>
        {canRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="text-xs text-red-500 hover:text-red-600 font-medium"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Remove
          </button>
        )}
      </div>
      <div title={isDisabled ? disabledReason : ''}>
        <input
          type="number"
          min={1}
          max={MAX_REMINDER_DAYS}
          value={value ?? ''}
          onChange={onChange}
          onBlur={(e) => {
            const raw = e.target.value;
            if (raw === '' || raw === null) { onBlurUpdate(null); return; }
            const n = parseInt(raw, 10);
            if (Number.isNaN(n)) { onBlurUpdate(null); return; }
            onBlurUpdate(Math.max(1, Math.min(MAX_REMINDER_DAYS, n)));
          }}
          disabled={isDisabled}
          placeholder={`Enter days (1-${MAX_REMINDER_DAYS})`}
          className={`input input-md ${error ? 'input-invalid' : ''} ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          style={{ fontFamily: 'Inter, sans-serif', color: '#0e1e20' }}
        />
      </div>
      {error && <p className="text-red-600 -mt-1 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>{error}</p>}
      {isDisabled && disabledReason && (
        <p className="text-xs text-amber-600 mt-1" style={{ fontFamily: 'Inter, sans-serif' }}>{disabledReason}</p>
      )}
    </div>
  );
}

export default function RemindersTab({ mediatorId, notify }) {
  const [reminders, setReminders] = useState({ onboarding: {}, brief: {}, payment: {} });
  const [isOdr, setIsOdr] = useState(false);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({ onboarding: 1, brief: 1, payment: 1 });
  const [pendingAction, setPendingAction] = useState(null);
  // Seed the row counts once, from the first load only — same as Scale, whose
  // counters are useState with no re-sync. Re-seeding on every refetch would
  // make a row the user just added disappear the moment a save came back.
  const seeded = useRef(false);

  const fetchReminders = useCallback(async () => {
    if (!mediatorId) { setLoading(false); return; }
    try {
      setLoading(true);
      const res = await api.get(`/mediators/${mediatorId}/reminders`);
      const data = res.data?.data || {};
      const s = data.reminder_schedules || {};
      const next = {
        onboarding: s.onboarding || {},
        brief: s.brief || {},
        payment: s.payment || {},
      };
      setReminders(next);
      setIsOdr(data.is_odr_mediator === true);
      if (!seeded.current) {
        setCounts({
          onboarding: initialActiveCount(next.onboarding),
          brief: initialActiveCount(next.brief),
          payment: initialActiveCount(next.payment),
        });
        seeded.current = true;
      }
    } catch (err) {
      notify?.({ type: 'error', message: err.response?.data?.message || 'Failed to load reminders' });
    } finally {
      setLoading(false);
    }
  }, [mediatorId, notify]);

  useEffect(() => { fetchReminders(); }, [fetchReminders]);

  // Build the whole object and send it. A null value deletes that one key and
  // leaves the others where they are — keys are never re-indexed, because
  // sliding reminder_3_days into slot 2 would move a reminder in time.
  const persist = async (base, reminderKey, value) => {
    const normalized = value === 0 || value === '' ? null : value;
    const nextCategory = { ...(reminders[base] || {}) };
    if (normalized === null) delete nextCategory[reminderKey];
    else nextCategory[reminderKey] = normalized;

    const updated = {};
    Object.values(CATEGORIES).forEach((c) => {
      const src = c === base ? nextCategory : (reminders[c] || {});
      const cleaned = {};
      Object.keys(src).forEach((k) => {
        const v = src[k];
        if (v !== null && v !== undefined && v !== '') cleaned[k] = v;
      });
      updated[c] = cleaned;
    });

    setReminders(updated); // optimistic, as Scale does
    try {
      await api.put(`/mediators/${mediatorId}/reminders`, { reminder_schedules: updated });
      notify?.({ type: 'success', message: 'Changes Saved' });
      return true;
    } catch (err) {
      notify?.({ type: 'error', message: err.response?.data?.message || 'Failed to save reminders' });
      fetchReminders();
      return false;
    }
  };

  const handleValueChange = (base, index, e, activeCount) => {
    const raw = e.target.value;
    // A non-last row cannot be blanked — the rows below it depend on it.
    if ((raw === '' || raw === null) && index < activeCount - 1) return;
    const next = { ...(reminders[base] || {}) };
    if (raw === '') delete next[keyFor(index)];
    else {
      const n = parseInt(raw, 10);
      if (Number.isNaN(n)) return;
      let clamped = Math.max(1, Math.min(MAX_REMINDER_DAYS, n));
      // Brief reminders all count BACK from the mediation date, so each one
      // must be closer to it than the one before — 3 then 10 would fire in the
      // wrong order. Scale refuses the keystroke and corrects on blur; we clamp
      // to one day nearer than the previous reminder, which is the same result.
      if (base === 'brief' && index > 0) {
        const previous = next[keyFor(index - 1)];
        if (typeof previous === 'number' && clamped >= previous) {
          clamped = Math.max(1, previous - 1);
        }
      }
      next[keyFor(index)] = clamped;
    }
    setReminders((prev) => ({ ...prev, [base]: next }));
  };

  const confirmProceed = () => {
    if (!pendingAction) return;
    const { type, base, indexToRemove } = pendingAction;
    if (type === 'add') {
      setCounts((prev) => ({ ...prev, [base]: Math.min(prev[base] + 1, MAX_REMINDERS) }));
    } else {
      // Only shrink the row count once the delete has actually landed. The row
      // counts are seeded once and never re-synced, so decrementing on a failed
      // save would hide a reminder that is still live on the server.
      persist(base, keyFor(indexToRemove), null).then((ok) => {
        if (ok) setCounts((prev) => ({ ...prev, [base]: Math.max(1, prev[base] - 1) }));
      });
    }
    setPendingAction(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center bg-white rounded-md shadow" style={{ padding: '48px 0' }}>
        <div className="w-6 h-6 border-2 border-[#115e59] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const renderGroup = (base, title, helper) => {
    const activeCount = counts[base];
    const categoryReminders = reminders[base] || {};
    const labelOrder = Array.from({ length: activeCount }, (_, i) => getOrdinalLabel(i + 1));
    const lastValue = categoryReminders[keyFor(activeCount - 1)];
    const isMaxReached = activeCount >= MAX_REMINDERS;
    const briefLastIsOne = base === 'brief' && lastValue === 1;
    const canAddNext =
      !isMaxReached && !briefLastIsOne
      && lastValue !== null && lastValue !== undefined && lastValue !== '';

    const addTooltip = isMaxReached
      ? `You can create up to ${MAX_REMINDERS} reminders`
      : briefLastIsOne
        ? 'Cannot add more reminders when the last reminder is 1 day'
        : !canAddNext
          ? 'Please enter a value for the current reminder before adding another'
          : '';

    return (
      <div className="flex flex-col gap-y-4 bg-white p-4 lg:p-6 rounded-md shadow">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-gray-900" style={{ fontFamily: 'Inter, sans-serif' }}>{title}</h4>
          <span title={addTooltip}>
            <button
              type="button"
              disabled={isMaxReached || !canAddNext}
              onClick={() => setPendingAction({ type: 'add', base })}
              className="button button-solid button-sm"
              style={{
                fontFamily: 'Inter, sans-serif',
                background: isMaxReached || !canAddNext ? '#e5e5e5' : '#115e59',
                color: isMaxReached || !canAddNext ? '#9ca3af' : '#fff',
                cursor: isMaxReached || !canAddNext ? 'not-allowed' : 'pointer',
              }}
            >
              + Add another reminder
            </button>
          </span>
        </div>

        <p className="text-xs text-gray-600 leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
          {helper}{' '}
          <span className="font-medium">
            Leave a field blank if you do not want to send that particular reminder.
          </span>
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Array.from({ length: activeCount }, (_, index) => {
            const isFirst = index === 0;
            const isLast = index === activeCount - 1;
            const isDisabled = index < activeCount - 1 && !isLast;
            const disabledReason = isDisabled
              ? 'Please remove all subsequent reminders before clearing this value'
              : '';

            let timingLabel;
            if (base === 'onboarding') {
              timingLabel = isFirst ? 'Days after case registration' : `Days after the ${labelOrder[index - 1]} reminder`;
            } else if (base === 'brief') {
              timingLabel = 'Days before the mediation date';
            } else if (isFirst) {
              timingLabel = 'Days after invoice creation';
            } else if (index === 1) {
              timingLabel = 'Days after invoice due';
            } else {
              timingLabel = `Days after ${labelOrder[index - 1]} reminder`;
            }

            const label = base === 'payment'
              ? getPaymentReminderLabel(index, labelOrder)
              : `${labelOrder[index]} ${title}`;

            return (
              <ReminderRow
                key={`${base}-${index}`}
                label={label}
                timingLabel={timingLabel}
                value={categoryReminders[keyFor(index)]}
                error={null}
                onChange={(e) => handleValueChange(base, index, e, activeCount)}
                onBlurUpdate={(val) => persist(base, keyFor(index), val)}
                canRemove={!isFirst && isLast}
                onRemove={() => setPendingAction({ type: 'remove', base, indexToRemove: index })}
                isDisabled={isDisabled}
                disabledReason={disabledReason}
              />
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div>
      <SectionHeading
        title="Reminder Schedules"
        subtitle="Configure reminder schedules for onboarding, briefs, and payments. Each reminder is sent relative to a key event, and any additional reminders are sent a set number of days after the previous one. Reminders are sent on business days only."
      />
      <div className="grid grid-cols-1 gap-6">
        <div>{renderGroup(CATEGORIES.ONBOARDING, 'Onboarding Reminder', ONBOARDING_HELPER)}</div>
        {!isOdr && <div>{renderGroup(CATEGORIES.BRIEF, 'Brief Reminder', BRIEF_HELPER)}</div>}
        <div>{renderGroup(CATEGORIES.PAYMENT, 'Payment Reminder', PAYMENT_HELPER)}</div>
      </div>
      <ConfirmDialog
        open={!!pendingAction}
        onCancel={() => setPendingAction(null)}
        onConfirm={confirmProceed}
      />
    </div>
  );
}
